import { Pipe, PipeTransform } from '@angular/core';
import { TranslateService } from '@ngx-translate/core';
import { RangeInterface } from '@sport-activities/sdk';

import { AgeRequirementService } from '~/core/services/activity/age-requirement.service';

const MAX_AGE = 100;
const MIN_AGE = 0;

/*
 * Display an age range
 * Usage:
 *   ageRange | displayAgeRange
 * Example:
 *   {{ { min = 0, max = 100 } | displayAgeRange }}
 *   formats to: tout public
 *   {{ { min = 7, max = 100 } | displayAgeRange }}
 *   formats to: à partir de 7ans
 *   {{ { min = 18, max = 100 } | displayAgeRange }}
 *   formats to: adulte
 *   {{ { min = 1, max = 99 } | displayAgeRange }}
 *   formats to: 1 à 99 ans
 *   {{ { min = 20, max = 30 } | displayAgeRange }}
 *   formats to: 20 à 30 ans
 *   {{ { min = 6, max = 12 } | displayAgeRange }}
 *   formats to: 6 à 12 ans
 */
@Pipe({ name: 'displayAgeRange' })
export class DisplayAgeRangePipe implements PipeTransform {
  constructor(
    private readonly translateService: TranslateService,
    private readonly ageRequirement: AgeRequirementService
  ) {}

  public transform(ageRange: RangeInterface): string {
    const majority = +this.ageRequirement.tenantMajority;

    const { min = 0, max = 99 } = ageRange;

    if (min === MIN_AGE && max === MAX_AGE) {
      return this.translateService.instant(
        'activity_creation.audience.audience.all.text'
      );
    }

    if (min > MIN_AGE && min < majority && max === MAX_AGE) {
      return this.translateService
        .instant('activity_creation.audience.audience.range.text')
        .replace(':age', min);
    }

    if (min >= majority && max === MAX_AGE) {
      return this.translateService.instant(
        'activity_creation.audience.audience.adults.text',
        { minAge: min }
      );
    }

    return this.translateService.instant('age_range', {
      minAge: min,
      maxAge: max,
    });
  }
}
